import { $ } from "@jpj/game-shell";
import type { Stage } from "../three/scene";
import { canPickUp, landing, pieceAt, type Landing } from "./board";
import { LOOT } from "./items";
import { island, renderQuay, setHint, updateHud } from "./flow";
import { state } from "./state";
import type { PlacedLoot } from "./types";

/**
 * Pointer + keyboard mechanics: pick loot up from the quay (or back off the
 * deck), steer the placement ghost, rotate, drop. Dragging orbits the camera
 * (OrbitControls); a *tap* — little movement, short press — places or picks.
 */

let stage: Stage;
let lastPointer: { x: number; y: number } | null = null;
let hover: { x: number; y: number; landing: Landing } | null = null;

/** PROTOTYPE (under review): drag a loot card from the quay straight onto the
 *  deck. Tap-to-select + tap-to-place still works as the fallback. */
let drag: { shelfIdx: number; started: boolean; sx: number; sy: number; pointerId: number } | null = null;
let justDraggedAt = 0;

const heldType = () => (state.selected === null ? null : LOOT[island().loot[state.selected]]);
const heldDims = (): { w: number; d: number; h: number } | null => {
  const t = heldType();
  if (!t) return null;
  return state.rotated ? { w: t.d, d: t.w, h: t.h } : { w: t.w, d: t.d, h: t.h };
};

const FAULT_TEXT: Record<string, string> = {
  uneven: "The ground under it must be level — no tile over thin air.",
  fragile: "That top bears nothing. Find a flat lid or the deck.",
  height: "Too high — the bosun must see the bow. Keep it under the line.",
  bounds: "Keep it inside the roped square.",
};

function showFault(fault?: string, blocker?: string): void {
  const el = $("ghostMsg");
  if (!fault) { el.classList.add("hidden"); return; }
  el.textContent = fault === "fragile" && blocker
    ? `Nothing stacks on a ${blocker.toLowerCase()}.`
    : FAULT_TEXT[fault] ?? "It won't sit there.";
  el.classList.remove("hidden");
}

function refreshGhost(): void {
  if (state.phase !== "play") return;
  const dims = heldDims();
  if (!dims || !lastPointer) return;
  const cell = stage.pickCell(lastPointer.x, lastPointer.y);
  if (!cell) {
    hover = null;
    stage.hideGhost();
    showFault();
    return;
  }
  const { cols, rows } = state.deck;
  const x = Math.max(0, Math.min(cell.x - Math.floor((dims.w - 1) / 2), cols - dims.w));
  const y = Math.max(0, Math.min(cell.y - Math.floor((dims.d - 1) / 2), rows - dims.d));
  const l = landing(state.deck, x, y, dims.w, dims.d, dims.h);
  hover = { x, y, landing: l };
  stage.moveGhost(x, y, l.z, l.ok, l.fault === "height");
  const blocker = l.fault === "fragile" ? findFragileBlocker(x, y, dims.w, dims.d, l.z) : undefined;
  showFault(l.ok ? undefined : l.fault, blocker);
}

function findFragileBlocker(x: number, y: number, w: number, d: number, z: number): string | undefined {
  for (let dx = 0; dx < w; dx++) for (let dy = 0; dy < d; dy++) {
    const p = pieceAt(state.deck, x + dx, y + dy, z - 1);
    if (p && !p.stackable) return LOOT[p.key]?.name;
  }
  return undefined;
}

function setSelected(idx: number | null, rotated = false): void {
  state.selected = idx;
  state.rotated = rotated;
  const dims = heldDims();
  const t = heldType();
  stage.setGhost(t ? t.key : null, dims?.w, dims?.d);
  if (t) setHint(`${t.name} in hand — tap the deck to load it. ${t.w !== t.d ? "⟳ rotates it. " : ""}Tap it again on the quay to put it down.`);
  else setHint("Drag loot from the quay onto the deck — or tap a card, then tap the deck. Flat tops carry more.");
  renderQuay();
  refreshGhost();
}

function placeHeld(): void {
  const t = heldType();
  const dims = heldDims();
  if (!t || !dims || !hover || !hover.landing.ok || state.selected === null) return;
  const p: PlacedLoot = {
    uid: state.nextUid++,
    shelfIdx: state.selected,
    key: t.key,
    value: t.value,
    stackable: t.stackable,
    x: hover.x, y: hover.y, z: hover.landing.z,
    w: dims.w, d: dims.d, h: dims.h,
  };
  state.deck.placed.push(p);
  state.shelf[state.selected] = p.uid;
  stage.addLoot(p);
  showFault();
  updateHud();
  // hand over the next copy of the same loot, if one still waits on the quay
  const loot = island().loot;
  const next = loot.findIndex((key, i) => key === t.key && state.shelf[i] === null);
  setSelected(next === -1 ? null : next, state.rotated && next !== -1);
}

function tryPickUp(uid: number): void {
  const p = state.deck.placed.find((q) => q.uid === uid);
  if (!p) return;
  if (!canPickUp(state.deck, p)) {
    stage.shakeLoot(uid);
    const el = $("ghostMsg");
    el.textContent = `Take off what rests on the ${LOOT[p.key].name.toLowerCase()} first.`;
    el.classList.remove("hidden");
    setTimeout(() => el.classList.add("hidden"), 1600);
    return;
  }
  state.deck.placed = state.deck.placed.filter((q) => q.uid !== uid);
  state.shelf[p.shelfIdx] = null;
  stage.removeLoot(uid);
  updateHud();
  const t = LOOT[p.key];
  setSelected(p.shelfIdx, t.w !== t.d && p.w !== t.w);
}

export function quayTap(shelfIdx: number): void {
  if (state.phase !== "play" || shelfIdx < 0) return;
  // swallow the click the browser synthesises at the end of a drag-to-place
  if (performance.now() - justDraggedAt < 350) return;
  const uid = state.shelf[shelfIdx];
  if (uid !== null) { tryPickUp(uid); return; }
  setSelected(state.selected === shelfIdx ? null : shelfIdx);
}

export function rotateHeld(): void {
  const t = heldType();
  if (!t || t.w === t.d) return;
  setSelected(state.selected, !state.rotated);
}

/**
 * PROTOTYPE control: press a loot card on the quay and drag it onto the deck.
 * The existing ghost / landing physics are reused unchanged; the camera orbit
 * is frozen while a piece is in flight and released on drop. Pointer capture is
 * taken only once a real drag begins (past a small threshold) so a plain tap
 * still falls through to the card's click → quayTap (select / pick up).
 */
function initQuayDrag(): void {
  const quay = $("quay");
  quay.addEventListener("pointerdown", (e: PointerEvent) => {
    if (state.phase !== "play" || (e.pointerType === "mouse" && e.button !== 0)) return;
    const card = (e.target as HTMLElement).closest(".loot-card") as HTMLElement | null;
    if (!card || card.classList.contains("depleted")) return;
    const shelfIdx = Number(card.dataset.shelf);
    if (shelfIdx < 0) return;                          // type fully aboard — nothing to take
    drag = { shelfIdx, started: false, sx: e.clientX, sy: e.clientY, pointerId: e.pointerId };
  });
  quay.addEventListener("pointermove", (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    if (!drag.started) {
      const dx = e.clientX - drag.sx, dy = e.clientY - drag.sy;
      if (dx * dx + dy * dy < 64) return;            // 8px — still a tap, let click handle it
      drag.started = true;
      try { quay.setPointerCapture(drag.pointerId); } catch { /* no-op */ }
      stage.controls.enabled = false;
      document.body.classList.add("pd-dragging");
      if (state.selected !== drag.shelfIdx) setSelected(drag.shelfIdx);
    }
    lastPointer = { x: e.clientX, y: e.clientY };
    refreshGhost();
  });
  const finish = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    const wasDrag = drag.started;
    try { quay.releasePointerCapture(drag.pointerId); } catch { /* no-op */ }
    drag = null;
    if (!wasDrag) return;                            // a tap → the card's click selects / picks up
    stage.controls.enabled = true;
    document.body.classList.remove("pd-dragging");
    justDraggedAt = performance.now();
    lastPointer = { x: e.clientX, y: e.clientY };
    refreshGhost();
    if (hover?.landing.ok) placeHeld();              // dropped on a valid cell → load it
    else stage.hideGhost();                          // missed: keep it in hand for a tap-place
  };
  quay.addEventListener("pointerup", finish);
  quay.addEventListener("pointercancel", finish);
}

export function initInteraction(s: Stage): void {
  stage = s;
  initQuayDrag();
  const canvas = stage.renderer.domElement;
  let down: { x: number; y: number; t: number } | null = null;

  canvas.addEventListener("pointerdown", (e) => {
    down = { x: e.clientX, y: e.clientY, t: performance.now() };
  });
  canvas.addEventListener("pointermove", (e) => {
    lastPointer = { x: e.clientX, y: e.clientY };
    if (state.phase !== "play") return;
    if (state.selected !== null) {
      refreshGhost();
    } else {
      const uid = stage.pickLoot(e.clientX, e.clientY);
      const p = uid === null ? null : state.deck.placed.find((q) => q.uid === uid);
      canvas.style.cursor = p && canPickUp(state.deck, p) ? "grab" : "";
    }
  });
  canvas.addEventListener("pointerup", (e) => {
    if (!down) return;
    const dx = e.clientX - down.x, dy = e.clientY - down.y;
    const isTap = dx * dx + dy * dy < 64 && performance.now() - down.t < 500;
    down = null;
    if (!isTap || state.phase !== "play") return;
    lastPointer = { x: e.clientX, y: e.clientY };
    if (state.selected !== null) {
      refreshGhost();
      if (hover?.landing.ok) placeHeld();
    } else {
      const uid = stage.pickLoot(e.clientX, e.clientY);
      if (uid !== null) tryPickUp(uid);
    }
  });
  canvas.addEventListener("pointerleave", () => {
    hover = null;
    stage.hideGhost();
    showFault();
  });

  window.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "r" || e.key === "R") rotateHeld();
    if (e.key === "Escape" && state.selected !== null) setSelected(null);
  });
}

/** Re-aim the ghost after programmatic changes (rotation button, island load). */
export function reaimGhost(): void {
  refreshGhost();
}
